import { ArrowRight, CalendarClock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatPlanned } from '@/lib/planningDates'
import { moveImpactText } from '@/lib/moveImpact'
import type { ProcedureHolds } from '@/api/proceduresApi'

/**
 * Asked before a scheduled date is saved.
 *
 * Changing a run's schedule is no longer only a date edit: it releases and
 * re-takes the equipment and material the run is holding. That consequence is
 * invisible from a date cell, so it is stated before the write rather than
 * discovered afterwards in the reserve dialog.
 *
 * **Opens before the counts arrive, on purpose.** From/to is known the instant
 * the operator picks a date, so the dialog can appear immediately and fill in
 * the impact line — the alternative is a second of nothing happening, which is
 * the complaint this whole flow exists to answer. Confirm stays disabled until
 * the counts land, so nobody agrees to a consequence they were not shown.
 *
 * Scoped to the SCHEDULED date. A due date books nothing, so confirming a
 * change to it would be friction with no consequence to warn about.
 */
export function MoveRunConfirmDialog({
  open,
  procedureName,
  from,
  to,
  holds,
  loading,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean
  procedureName: string
  /** The run's current scheduled start. Null when it has never been scheduled. */
  from: string | null
  /** The proposed start. Null when the operator is clearing the schedule. */
  to: string | null
  /** Null while still being fetched. */
  holds: ProcedureHolds | null
  loading: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const clearing = !to
  const title = clearing ? 'Clear this run’s schedule?' : from ? 'Move this run?' : 'Schedule this run?'

  return (
    <Dialog open={open} onOpenChange={o => !o && onCancel()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-base'>
            <CalendarClock className='h-4 w-4 shrink-0 text-blue-600' />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-3'>
          <p className='text-sm font-medium text-gray-800'>{procedureName}</p>

          {/* From/to is known immediately and renders immediately — it is the
              part that tells the operator their click registered. */}
          <div className='flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs'>
            <div className='min-w-0 flex-1'>
              <p className='text-[10px] uppercase tracking-wide text-gray-500'>From</p>
              <p className='truncate font-mono tabular-nums text-gray-700'>
                {from ? formatPlanned(from) : 'Not scheduled'}
              </p>
            </div>
            <ArrowRight className='h-3.5 w-3.5 shrink-0 text-gray-400' />
            <div className='min-w-0 flex-1'>
              <p className='text-[10px] uppercase tracking-wide text-gray-500'>To</p>
              <p className='truncate font-mono font-semibold tabular-nums text-gray-900'>
                {to ? formatPlanned(to) : 'Not scheduled'}
              </p>
            </div>
          </div>

          {loading ? (
            <p className='flex items-center gap-2 text-xs text-muted-foreground'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
              Checking what this run is holding…
            </p>
          ) : holds ? (
            <p className='text-xs text-gray-700'>{moveImpactText(holds, clearing)}</p>
          ) : null}

          {error && (
            <p
              role='alert'
              className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800'
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter className='gap-2 sm:gap-2'>
          <Button type='button' variant='ghost' onClick={onCancel}>
            Cancel
          </Button>
          {/* Disabled until the counts land: the whole point of this step is
              agreeing to a stated consequence. */}
          <Button type='button' onClick={onConfirm} disabled={loading}>
            {clearing ? 'Clear the schedule' : from ? 'Move the run' : 'Schedule it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
